import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { GalleryLightbox } from "@/components/GalleryLightbox";
import { workshopGalleryImages } from "@/data/workshopGallery";
import { ArrowRight, ExternalLink, Camera } from "lucide-react";
import { useState } from "react";

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStartIndex, setLightboxStartIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxStartIndex(index);
    setLightboxOpen(true);
  };

const projects = [
    {
      title: "Large Transport Storage Tank",
      client: "Industrial Manufacturing Corp",
      category: "Transportation",
      capacity: "50,000L",
      material: "SS316L",
      finishing: "Electropolished Interior",
      description: "Large-capacity storage tank designed for secure transportation with reinforced supports",
      image: "/lovable-uploads/b6485ff8-acdb-4d9f-8415-9f2abb3bf6ef.png",
      features: ["Transport-ready design", "Reinforced structure", "Compliance certified"]
    },
    {
      title: "Customer Fabrication Workshop Tank",
      client: "Process Engineering Solutions",
      category: "Custom Fabrication",
      capacity: "25,000L",
      material: "SS304",
      finishing: "R.a 0.4",
      description: "High-precision tank fabricated in our workshop with custom specifications and fittings",
      image: "/lovable-uploads/3d099546-68da-4ebe-a6ea-47fdf378b4e7.png",
      features: ["Custom specifications", "Precision welding", "Quality assured"]
    },
    {
      title: "Multi-Level Processing System",
      client: "Margarine Processing Industries",
      category: "Chemical Processing",
      capacity: "Multiple vessels",
      material: "SS316L",
      finishing: "Passivated Surface",
      description: "Complex multi-level processing system with integrated piping and control systems",
      image: "/lovable-uploads/5138a6e4-8dd2-4d1f-be3d-81a463bf2d08.png",
      features: ["Multi-level design", "Integrated systems", "Process optimization"]
    },
    {
      title: "Distillation Column System",
      client: "Petrochemical Solutions",
      category: "Distillation",
      capacity: "Industrial scale",
      material: "SS316L",
      finishing: "Industrial Grade",
      description: "Tall distillation column with structural framework for petrochemical processing",
      image: "/lovable-uploads/cfe4eb7c-c757-4f5e-b3e0-384a886706ad.png",
      features: ["High-pressure rated", "Structural support", "Process efficiency"]
    },
    {
      title: "Mobile Processing Units",
      client: "Dairy Manufacturing",
      category: "Mobile Systems",
      capacity: "50L - 5,000L",
      material: "SS304 or SS316L",
      finishing: "Sanitary Polish",
      description: "Mobile stainless steel tanks with wheels and integrated pumping systems for flexible processing",
      image: "/lovable-uploads/3d01fb44-e4a1-42a7-9920-fb4f315e157b.png",
      features: ["Mobile design", "Sanitary construction", "Integrated pumps"]
    },
    {
      title: "Vertical Storage System",
      client: "Food Processing Corp",
      category: "Storage",
      capacity: "30,000L",
      material: "SS304",
      finishing: "Food Grade",
      description: "Tall vertical storage tank with structural support framework for food industry applications",
      image: "/lovable-uploads/bee570e6-4592-4e46-bad3-30f7bf2275ce.png",
      features: ["Vertical efficiency", "Structural support", "Food grade materials"]
    },
    {
      title: "Pump Integration System",
      client: "Industrial Processing",
      category: "Pump Systems",
      capacity: "15,000L",
      material: "SS316L",
      finishing: "Industrial Polish",
      description: "Horizontal tank system with integrated blue pumps and control systems",
      image: "/lovable-uploads/bbc55e2f-cd86-4bcb-9ba4-56389f8df102.png",
      features: ["Pump integration", "Control systems", "Efficient transfer"]
    },
    {
      title: "Specialized Equipment Tank",
      client: "Pharmaceutical Industries",
      category: "Pharmaceuticals",
      capacity: "50L",
      material: "SS316L",
      finishing: "Mirror Polish",
      description: "Specialized tank with custom agitator and advanced process control equipment",
      image: "/lovable-uploads/ea1aee64-11de-4fc5-b6e6-dc11c5ad0593.png",
      features: ["Custom agitation", "Process controls", "Pharmaceutical grade"]
    },
    {
      title: "WLE Branded Production Tank",
      client: "Internal WLE Project",
      category: "Production",
      capacity: "8,000L",
      material: "SS316L",
      finishing: "WLE Standard",
      description: "High-quality production tank featuring WLE branding with integrated pump systems",
      image: "/lovable-uploads/1d87ab22-526b-42ac-857c-cfcfe0f564b4.png",
      features: ["WLE branded", "Production ready", "Integrated systems"]
    },
    {
      title: "Clean Room Processing System",
      client: "Biotech Manufacturing",
      category: "Clean Room",
      capacity: "Multiple vessels",
      material: "SS316L",
      finishing: "Electropolished",
      description: "Complete clean room processing system with multiple tanks and sterile connections",
      image: "/lovable-uploads/0749ac56-c59c-4484-86c1-bac8b3692007.png",
      features: ["Clean room design", "Sterile connections", "Multi-vessel system"]
    },
    {
      title: "Precision Agitator Systems",
      client: "Mixing Technology Corp",
      category: "Agitation Equipment",
      capacity: "Custom designed",
      material: "SS316L",
      finishing: "Mirror Polish",
      description: "Custom agitator blades and mixing systems with high-precision propellers and blue motors",
      image: "/lovable-uploads/94c6d394-ee7b-45f0-9e46-609b36a1921d.png",
      features: ["Custom propellers", "Variable speed motors", "Precision balanced"]
    },
    {
      title: "Tank Interior Agitator Assembly",
      client: "Process Engineering",
      category: "Internal Components",
      capacity: "Variable",
      material: "SS316L",
      finishing: "Electropolished",
      description: "High-precision internal agitator assembly showing WLE's expertise in internal tank components",
      image: "/lovable-uploads/23086d16-ca86-40a7-bc92-4a1d9a30117d.png",
      features: ["Precision machining", "Smooth operation", "Optimal mixing"]
    },
    {
      title: "Workshop Fabrication Project",
      client: "Industrial Client",
      category: "Workshop Services",
      capacity: "Large scale",
      material: "Stainless Steel",
      finishing: "Industrial Grade",
      description: "Large-scale tank fabrication project in WLE's workshop facility with structural support systems",
      image: "/lovable-uploads/85318cb9-3461-4ee7-9074-215f889c9c36.png",
      features: ["Workshop fabrication", "Structural support", "Quality control"]
    },
    {
      title: "Mechanical Surface Polishing",
      client: "Various Client",
      category: "Surface Finishing",
      capacity: "Various sizes",
      material: "SS304 or SS316L",
      finishing: "Mechanical Polished",
      description: "Professional mechanical polishing table and equipment for achieving mirror-finish surfaces",
      image: "/lovable-uploads/fe983bd6-89ac-4a9f-85f4-676b800aaeb8.png",
      features: ["Mirror finish", "Contamination free", "Pharmaceutical grade"]
    },
    {
      title: "Clean Room Tank Farm",
      client: "Biotech Industries",
      category: "Clean Room Systems",
      capacity: "Multiple 1,000L units",
      material: "SS316L",
      finishing: "Electropolished",
      description: "State-of-the-art clean room facility with multiple tanks and access walkways",
      image: "/lovable-uploads/aa87daeb-b72b-484d-b12c-69da0c6ba228.png",
      features: ["Clean room certified", "Easy access", "Sterile environment"]
    },
    {
      title: "Large Scale Manufacturing",
      client: "Industrial Corporation",
      category: "Mass Production",
      capacity: "Multiple large tanks",
      material: "Stainless Steel",
      finishing: "Industrial Polish",
      description: "Large-scale manufacturing facility showing multiple tank projects in various stages",
      image: "/lovable-uploads/9d192be7-fb11-42ca-86e7-cfa28a785f63.png",
      features: ["High capacity", "Multiple projects", "Efficient workflow"]
    },
    {
      title: "Tall Vertical Storage Tanks",
      client: "Storage Solutions Inc",
      category: "Vertical Storage",
      capacity: "50,000L each",
      material: "SS304",
      finishing: "Standard Polish",
      description: "Large vertical storage tanks with structural support framework and access platforms",
      image: "/lovable-uploads/61292f7b-9922-44fd-8d23-b15d5a2e7225.png",
      features: ["High capacity", "Structural support", "Safe access"]
    },
    {
      title: "Perforated Distributor Plate",
      client: "Process Equipment Corp",
      category: "Precision Components",
      capacity: "Custom specifications",
      material: "SS316L",
      finishing: "Precision Machined",
      description: "High-precision perforated distributor plate for uniform flow distribution in processing vessels",
      image: "/lovable-uploads/91ab2fb7-f40b-4c12-beb4-d3eafd9cf51b.png",
      features: ["Precision holes", "Uniform distribution", "Custom patterns"]
    },
    {
      title: "Multi-Level Processing Plant",
      client: "Chemical Processing Corp",
      category: "Complete Systems",
      capacity: "Industrial scale",
      material: "SS316L",
      finishing: "Process Grade",
      description: "Complete multi-level processing system with control panels and integrated equipment",
      image: "/lovable-uploads/e515f2c9-912e-4cf8-9292-074a4f8be578.png",
      features: ["Multi-level design", "Process controls", "Integrated systems"]
    },
    {
      title: "Insulated Storage Tank",
      client: "Temperature Control Systems",
      category: "Thermal Insulation",
      capacity: "40,000L",
      material: "Carbon Steel",
      finishing: "Insulated",
      description: "Large storage tank with thermal insulation system for temperature-sensitive applications",
      image: "/lovable-uploads/0ad40805-65dc-4f47-abb4-168c03512b04.png",
      features: ["Thermal insulation", "Temperature control", "Energy efficient"]
    },
    {
      title: "Precision Welding Services",
      client: "Quality Manufacturing Corp",
      category: "Welding Services",
      capacity: "All sizes",
      material: "SS316L",
      finishing: "Precision Welded",
      description: "Demonstrating uncompromising weld quality by certified welders ensuring highest quality joints",
      image: "/lovable-uploads/fa5cdf94-1700-4b7e-88f7-66beef3edecd.png",
      features: ["Certified welders", "TIG welding", "Quality assured"]
    },
    {
      title: "Custom Tank Head Assembly",
      client: "Process Equipment Corp",
      category: "Custom Components",
      capacity: "Custom specifications",
      material: "SS316L",
      finishing: "Mirror Polish",
      description: "Custom fabricated tank head with multiple ports, sight glasses, and specialized fittings",
      image: "/lovable-uploads/92025a57-2bdd-4ba1-a761-54d09eec9c6a.png",
      features: ["Multiple ports", "Custom design", "Precision fittings"]
    },
    {
      title: "Multi-Port Tank End",
      client: "Pharmaceutical Industries",
      category: "Pharmaceutical Components",
      capacity: "Various sizes",
      material: "SS316L",
      finishing: "Electropolished",
      description: "Sophisticated tank end with multiple process connections and viewing ports for pharmaceutical applications",
      image: "/lovable-uploads/c08816f8-bea1-4836-a11b-4b3ddc6d38dc.png",
      features: ["Pharmaceutical grade", "Multiple connections", "Sterile design"]
    },
    {
      title: "Elevated Mixing Platform",
      client: "Chemical Processing Ltd",
      category: "Platform Systems",
      capacity: "Industrial scale",
      material: "SS304",
      finishing: "Industrial Grade",
      description: "Elevated mixing platform with safety railings and integrated equipment for safe operation",
      image: "/lovable-uploads/29125048-112b-466c-ab43-783d1654f4e4.png",
      features: ["Safety railings", "Elevated design", "Integrated systems"]
    },
    {
      title: "Precision Brewing Tank",
      client: "Brewery Solutions",
      category: "Brewing Equipment",
      capacity: "5,000L",
      material: "SS304",
      finishing: "Food Grade Polish",
      description: "High-quality brewing tank with conical bottom and polished finish for beverage production",
      image: "/lovable-uploads/17466fd6-2042-4760-a5cb-fbb731463cdc.png",
      features: ["Conical bottom", "Food grade", "Brewing optimized"]
    },
    {
      title: "Brewery Tank Farm",
      client: "Commercial Brewery",
      category: "Brewing Systems",
      capacity: "Multiple 3,000L units",
      material: "SS304",
      finishing: "Brewery Standard",
      description: "Complete brewery tank farm with multiple fermentation vessels for large-scale beer production",
      image: "/lovable-uploads/c0a59953-e613-42c3-bc81-b760ba682632.png",
      features: ["Multiple tanks", "Brewery design", "Production scale"]
    },
    {
      title: "Industrial Tank Farm",
      client: "Beverage Manufacturing",
      category: "Tank Farms",
      capacity: "Multiple large vessels",
      material: "SS304",
      finishing: "Industrial Polish",
      description: "Large-scale tank farm installation with multiple vessels for industrial beverage production",
      image: "/lovable-uploads/6ba30429-8b9f-4df3-8893-47f83ea5616f.png",
      features: ["Large scale", "Multiple vessels", "Industrial grade"]
    },
    {
      title: "Large Storage Silo Installation",
      client: "Industrial Storage Corp",
      category: "Silo Installation",
      capacity: "100,000L+",
      material: "Carbon Steel",
      finishing: "Industrial Coating",
      description: "Massive storage silo installation with crane operations for bulk material storage",
      image: "/lovable-uploads/b95f8eba-d4a4-4c84-a0f6-3d6d156d03ec.png",
      features: ["Massive capacity", "Crane installation", "Bulk storage"]
    },
    {
      title: "Advanced Process Reactor",
      client: "Biotech Research",
      category: "Research Equipment",
      capacity: "2,000L",
      material: "SS316L",
      finishing: "Mirror Polish",
      description: "Sophisticated process reactor with advanced controls, sight glass, and mobile design for research applications",
      image: "/lovable-uploads/cdad7a2d-91df-41d8-93f2-7a8d2d8cf543.png",
      features: ["Advanced controls", "Sight glass", "Research grade"]
    },
    {
      title: "Precision Sight Glass Assembly",
      client: "Process Monitoring Corp",
      category: "Viewing Components",
      capacity: "Custom specifications",
      material: "SS316L",
      finishing: "Mirror Polish",
      description: "High-precision sight glass assembly with bolted flange design for process monitoring applications",
      image: "/lovable-uploads/c3061799-6a66-463a-8943-6ec1de98e75e.png",
      features: ["Precision machined", "Bolted flange", "Process monitoring"]
    }
  ];

  const categories = ["All", "Transportation", "Custom Fabrication", "Chemical Processing", "Distillation", "Mobile Systems", "Storage", "Pump Systems", "Pharmaceuticals", "Production", "Clean Room", "Agitation Equipment", "Internal Components", "Workshop Services", "Surface Finishing", "Clean Room Systems", "Mass Production", "Vertical Storage", "Precision Components", "Complete Systems", "Thermal Insulation", "Welding Services", "Custom Components", "Pharmaceutical Components", "Platform Systems", "Brewing Equipment", "Brewing Systems", "Tank Farms", "Silo Installation", "Research Equipment", "Viewing Components"];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Transportation": return "bg-blue-100 text-blue-800";
      case "Custom Fabrication": return "bg-green-100 text-green-800";
      case "Chemical Processing": return "bg-orange-100 text-orange-800";
      case "Distillation": return "bg-purple-100 text-purple-800";
      case "Mobile Systems": return "bg-yellow-100 text-yellow-800";
      case "Storage": return "bg-indigo-100 text-indigo-800";
      case "Pump Systems": return "bg-cyan-100 text-cyan-800";
      case "Pharmaceuticals": return "bg-wle-blue/10 text-wle-blue";
      case "Production": return "bg-wle-red/10 text-wle-red";
      case "Clean Room": return "bg-pink-100 text-pink-800";
      case "Agitation Equipment": return "bg-teal-100 text-teal-800";
      case "Internal Components": return "bg-slate-100 text-slate-800";
      case "Workshop Services": return "bg-amber-100 text-amber-800";
      case "Surface Finishing": return "bg-emerald-100 text-emerald-800";
      case "Clean Room Systems": return "bg-rose-100 text-rose-800";
      case "Mass Production": return "bg-violet-100 text-violet-800";
      case "Vertical Storage": return "bg-sky-100 text-sky-800";
      case "Precision Components": return "bg-lime-100 text-lime-800";
      case "Complete Systems": return "bg-fuchsia-100 text-fuchsia-800";
      case "Thermal Insulation": return "bg-stone-100 text-stone-800";
      case "Welding Services": return "bg-red-100 text-red-800";
      case "Custom Components": return "bg-blue-200 text-blue-900";
      case "Pharmaceutical Components": return "bg-green-200 text-green-900";
      case "Platform Systems": return "bg-yellow-200 text-yellow-900";
      case "Brewing Equipment": return "bg-orange-200 text-orange-900";
      case "Brewing Systems": return "bg-amber-200 text-amber-900";
      case "Tank Farms": return "bg-emerald-200 text-emerald-900";
      case "Silo Installation": return "bg-gray-200 text-gray-900";
      case "Research Equipment": return "bg-purple-200 text-purple-900";
      case "Viewing Components": return "bg-indigo-200 text-indigo-900";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Portfolio | Tank Fabrication Projects & Gallery - WLE Technology</title>
        <meta name="description" content="Explore WLE Technology's portfolio of successful tank fabrication projects. Pharmaceutical vessels, industrial tanks, clean room systems, and more across Malaysia." />
        <link rel="canonical" href="https://wletec.com/portfolio" />
        <meta property="og:title" content="WLE Technology Portfolio - Tank Fabrication Projects" />
        <meta property="og:description" content="View our successful tank fabrication projects across various industries." />
        <meta property="og:url" content="https://wletec.com/portfolio" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="tank fabrication portfolio, project gallery, pharmaceutical tanks, industrial tanks, Malaysia" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Our <span className="text-wle-blue">Portfolio</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore our successful projects across various industries. From pharmaceutical-grade vessels 
              to large-scale industrial tanks, each project showcases our commitment to quality and precision.
            </p>
          </div>
        </div>
      </section>

      {/* From our Workshop Gallery */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              From Our <span className="text-wle-blue">Workshop</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Behind the scenes at WLE - witness the precision, craftsmanship, and attention to detail 
              that goes into every project we deliver.
            </p>
          </div>
          
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {workshopGalleryImages.map((image, index) => (
              <div 
                key={index}
                className="break-inside-avoid cursor-pointer relative overflow-hidden rounded-lg hover:scale-105 transition-transform duration-300"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent && !parent.querySelector('.image-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'image-fallback w-full h-auto bg-muted flex items-center justify-center p-8';
                      fallback.innerHTML = '<span class="text-muted-foreground">Image not available</span>';
                      parent.appendChild(fallback);
                    }
                  }}
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <Camera className="text-white opacity-0 hover:opacity-100 transition-opacity duration-300 h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Categories */}
      <section className="py-8">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">
              Browse Our <span className="text-wle-red">Projects</span>
            </h2>
            <p className="text-muted-foreground">
              Filter by category to explore specific types of tank fabrication and processing equipment
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="hover:bg-wle-blue hover:text-white"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-8">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8">
            {projects
              .filter(project => selectedCategory === "All" || project.category === selectedCategory)
              .map((project, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-elegant transition-all duration-300">
                <div className="aspect-video bg-muted relative">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent && !parent.querySelector('.image-fallback')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'image-fallback w-full h-full flex items-center justify-center bg-muted';
                        fallback.innerHTML = '<span class="text-muted-foreground">Image not available</span>';
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className={getCategoryColor(project.category)}>
                      {project.category}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{project.title}</CardTitle>
                  <CardDescription className="text-sm font-medium text-wle-blue">
                    {project.client}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{project.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm font-medium">Capacity</p>
                      <p className="text-sm text-muted-foreground">{project.capacity}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Material</p>
                      <p className="text-sm text-muted-foreground">{project.material}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm font-medium">Finishing</p>
                      <p className="text-sm text-muted-foreground">{project.finishing}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm font-medium mb-3">Key Features</p>
                    <ul className="space-y-2">
                      {project.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-wle-blue flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button variant="outline" className="w-full">
                    View Details
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="container">
          <Card className="bg-gradient-primary text-white">
            <CardContent className="p-12">
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold mb-2">500+</div>
                  <div className="opacity-90">Projects Completed</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">25+</div>
                  <div className="opacity-90">Years Experience</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">100+</div>
                  <div className="opacity-90">Happy Clients</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">99%</div>
                  <div className="opacity-90">Customer Satisfaction</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container">
          <Card>
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h3>
              <p className="text-lg text-muted-foreground mb-8">
                Join our portfolio of successful clients. Let's discuss your tank fabrication needs 
                and create a custom solution that meets your specifications.
              </p>
              <Button size="lg">
                Start Your Project
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
      
      {/* Gallery Lightbox */}
      <GalleryLightbox
        items={workshopGalleryImages}
        startIndex={lightboxStartIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default Portfolio;